import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Description, Gavel, Copyright, Warning, CheckCircle } from '@mui/icons-material';

const Terms = () => {
    return (
        <>
            <Helmet>
                <title>Conditions d'Utilisation - Netikash Developer Portal</title>
                <meta name="description" content="Conditions générales d'utilisation de l'API Netikash : droits, responsabilités, limitations et acceptation des conditions." />
                <meta property="og:title" content="Conditions d'Utilisation - Netikash" />
                <meta property="og:description" content="Conditions générales d'utilisation de l'API et des services Netikash." />
                <link rel="canonical" href="https://dev.netikash.com/terms" />
            </Helmet>
            <div className="min-h-screen bg-gray-50 dark:bg-slate-950 dark:text-slate-100 pt-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    {/* Header */}
                    <div className="mb-12 text-center">
                        <div className="inline-flex items-center px-4 py-2 rounded-full bg-amber-100 text-amber-800 mb-4">
                            <Gavel className="mr-2" fontSize="small" />
                            <span className="text-sm font-medium">Conditions Générales</span>
                        </div>
                        <h1 className="text-4xl font-bold text-gray-900 mb-4">Conditions d'Utilisation</h1>
                        <p className="text-xl text-gray-600">
                            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                    </div>

                    {/* Introduction */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-8">
                        <p className="text-gray-700 leading-relaxed mb-4">
                            Les présentes Conditions d'Utilisation ("Conditions") régissent votre accès et votre utilisation de l'API Netikash, de notre portail développeur et de tous les services associés (collectivement, les "Services").
                        </p>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                            <p className="text-amber-900 text-sm font-semibold">
                                En accédant ou en utilisant nos Services, vous acceptez d'être lié par ces Conditions. Si vous n'acceptez pas ces Conditions, vous ne devez pas utiliser nos Services.
                            </p>
                        </div>
                    </div>

                    {/* Sections */}
                    <div className="space-y-8">
                        {/* 1. Acceptation des conditions */}
                        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                                    <CheckCircle className="text-blue-600" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900">1. Acceptation des Conditions</h2>
                            </div>
                            <div className="space-y-4 text-gray-700">
                                <p>
                                    En créant un compte développeur, en accédant à notre portail développeur ou en utilisant l'API Netikash, vous confirmez que :
                                </p>
                                <ul className="list-disc list-inside space-y-2 ml-4">
                                    <li>Vous avez lu, compris et accepté ces Conditions d'Utilisation</li>
                                    <li>Vous avez la capacité légale d'accepter ces Conditions</li>
                                    <li>Vous utilisez nos Services conformément à toutes les lois et réglementations applicables</li>
                                    <li>Les informations que vous fournissez sont exactes et à jour</li>
                                </ul>
                            </div>
                        </section>

                        {/* 2. Description des services */}
                        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                                    <Description className="text-green-600" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900">2. Description des Services</h2>
                            </div>
                            <div className="space-y-4 text-gray-700">
                                <p>Netikash fournit :</p>
                                <ul className="list-disc list-inside space-y-2 ml-4">
                                    <li><strong>API de paiement</strong> : Services permettant l'intégration de paiements dans vos applications</li>
                                    <li><strong>Portail développeur</strong> : Documentation, outils et ressources pour les développeurs</li>
                                    <li><strong>Services d'authentification</strong> : Gestion des identifiants API et authentification OAuth2</li>
                                    <li><strong>Webhooks</strong> : Notifications en temps réel sur les transactions</li>
                                </ul>
                                <p className="text-sm text-gray-600 mt-4">
                                    Nous nous réservons le droit de modifier, suspendre ou interrompre tout ou partie de nos Services à tout moment, avec ou sans préavis.
                                </p>
                            </div>
                        </section>

                        {/* 3. Compte développeur */}
                        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">3. Compte Développeur</h2>
                            <div className="space-y-4 text-gray-700">
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-2">3.1 Création de compte</h3>
                                    <p>Pour utiliser nos Services, vous devez créer un compte développeur et fournir des informations exactes, complètes et à jour.</p>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-2">3.2 Sécurité du compte</h3>
                                    <p>Vous êtes responsable de :</p>
                                    <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                                        <li>La confidentialité de vos identifiants de connexion</li>
                                        <li>La protection de vos clés API (Client ID et Client Secret)</li>
                                        <li>Toutes les activités qui se produisent sous votre compte</li>
                                        <li>La notification immédiate de toute utilisation non autorisée</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-2">3.3 Utilisation des clés API</h3>
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-2">
                                        <p className="text-red-900 text-sm font-semibold mb-2">Avertissement de sécurité</p>
                                        <ul className="list-disc list-inside space-y-1 text-sm text-red-800">
                                            <li>Ne partagez jamais vos clés API avec des tiers</li>
                                            <li>Ne commitez jamais vos clés API dans votre code source public</li>
                                            <li>Utilisez des variables d'environnement pour stocker vos clés</li>
                                            <li>En cas de compromission, révoquez immédiatement les clés compromises</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* 4. Utilisation acceptable */}
                        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                                    <CheckCircle className="text-purple-600" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900">4. Utilisation Acceptable</h2>
                            </div>
                            <div className="space-y-4 text-gray-700">
                                <p>Vous acceptez de NE PAS utiliser nos Services pour :</p>
                                <div className="grid md:grid-cols-2 gap-4 mt-4">
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                        <h3 className="font-semibold text-red-900 mb-2">Activités interdites</h3>
                                        <ul className="list-disc list-inside space-y-1 text-sm text-red-800">
                                            <li>Violer des lois ou réglementations</li>
                                            <li>Faciliter des activités frauduleuses</li>
                                            <li>Collecter des données sans consentement</li>
                                            <li>Transmettre des virus ou codes malveillants</li>
                                            <li>Tenter d'accéder à des systèmes non autorisés</li>
                                            <li>Effectuer des attaques par déni de service</li>
                                        </ul>
                                    </div>
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                        <h3 className="font-semibold text-amber-900 mb-2">Respect des limites</h3>
                                        <ul className="list-disc list-inside space-y-1 text-sm text-amber-800">
                                            <li>Respecter les limites de taux (rate limits)</li>
                                            <li>Ne pas surcharger nos serveurs</li>
                                            <li>Utiliser les Services de manière raisonnable</li>
                                            <li>Respecter la propriété intellectuelle</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* 5. Propriété intellectuelle */}
                        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                                    <Copyright className="text-indigo-600" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900">5. Propriété Intellectuelle</h2>
                            </div>
                            <div className="space-y-4 text-gray-700">
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-2">5.1 Propriété de Netikash</h3>
                                    <p>
                                        Tous les droits, titres et intérêts concernant les Services, y compris mais sans s'y limiter, la documentation, l'API, les logos, les marques, et tout contenu du portail développeur, appartiennent à Netikash ou à ses concédants de licence.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-2">5.2 Licence d'utilisation</h3>
                                    <p>
                                        Netikash vous accorde une licence limitée, non exclusive, non transférable et révocable pour accéder et utiliser nos Services conformément à ces Conditions. Cette licence ne vous confère aucun droit de propriété sur les Services.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-2">5.3 Restrictions</h3>
                                    <p>Vous ne pouvez pas :</p>
                                    <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                                        <li>Copier, modifier ou créer des œuvres dérivées des Services</li>
                                        <li>Reverse engineer, décompiler ou désassembler nos Services</li>
                                        <li>Utiliser nos marques ou logos sans autorisation écrite</li>
                                        <li>Rendre disponibles nos Services à des tiers via un service de revendeur</li>
                                    </ul>
                                </div>
                            </div>
                        </section>

                        {/* 6. Limitations de responsabilité */}
                        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                                    <Warning className="text-yellow-600" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900">6. Limitations de Responsabilité</h2>
                            </div>
                            <div className="space-y-4 text-gray-700">
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <p className="text-yellow-900 font-semibold mb-2">Avis de non-responsabilité</p>
                                    <p className="text-yellow-800 text-sm">
                                        Les Services sont fournis "EN L'ÉTAT" et "TELS QUE DISPONIBLES", sans garantie d'aucune sorte, expresse ou implicite.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-2">6.1 Garanties exclues</h3>
                                    <p>Netikash ne garantit pas que :</p>
                                    <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                                        <li>Les Services fonctionneront sans interruption ou erreur</li>
                                        <li>Les défauts seront corrigés</li>
                                        <li>Les Services répondront à vos besoins spécifiques</li>
                                        <li>Les Services sont exempts de virus ou autres composants nuisibles</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-2">6.2 Limitation de responsabilité</h3>
                                    <p>
                                        Dans la mesure maximale permise par la loi, Netikash ne sera pas responsable des dommages indirects, accessoires, spéciaux, consécutifs ou punitifs résultant de votre utilisation ou de votre incapacité à utiliser les Services.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-2">6.3 Disponibilité des services</h3>
                                    <p>
                                        Nous nous efforçons de maintenir une disponibilité élevée de nos Services, mais nous ne garantissons pas une disponibilité de 100%. Nous pouvons interrompre les Services pour maintenance, mises à jour ou pour des raisons techniques.
                                    </p>
                                </div>
                            </div>
                        </section>

                        {/* 7. Indemnisation */}
                        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">7. Indemnisation</h2>
                            <div className="space-y-4 text-gray-700">
                                <p>
                                    Vous acceptez d'indemniser, de défendre et de dégager Netikash de toute responsabilité concernant toute réclamation, perte, responsabilité, dommage, coût ou dépense (y compris les frais d'avocat) résultant de :
                                </p>
                                <ul className="list-disc list-inside space-y-2 ml-4">
                                    <li>Votre utilisation des Services</li>
                                    <li>Votre violation de ces Conditions</li>
                                    <li>Votre violation de tout droit d'un tiers</li>
                                    <li>Votre violation de toute loi ou réglementation applicable</li>
                                </ul>
                            </div>
                        </section>

                        {/* 8. Résiliation */}
                        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">8. Résiliation</h2>
                            <div className="space-y-4 text-gray-700">
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-2">8.1 Résiliation par vous</h3>
                                    <p>Vous pouvez résilier votre compte à tout moment en nous contactant ou via votre espace développeur.</p>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-2">8.2 Résiliation par Netikash</h3>
                                    <p>Nous pouvons suspendre ou résilier votre accès aux Services immédiatement, sans préavis, si vous :</p>
                                    <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                                        <li>Violer ces Conditions d'Utilisation</li>
                                        <li>Utiliser les Services de manière frauduleuse ou illégale</li>
                                        <li>Ne pas payer les frais dus (le cas échéant)</li>
                                        <li>Compromettre la sécurité de nos Services</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-2">8.3 Effets de la résiliation</h3>
                                    <p>En cas de résiliation, votre droit d'accès aux Services cessera immédiatement. Les sections qui, par leur nature, doivent survivre après la résiliation continueront à s'appliquer.</p>
                                </div>
                            </div>
                        </section>

                        {/* 9. Droit applicable */}
                        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">9. Droit Applicable et Juridiction</h2>
                            <div className="space-y-4 text-gray-700">
                                <p>
                                    Ces Conditions sont régies et interprétées conformément aux lois de la République Démocratique du Congo. Tout litige découlant de ces Conditions sera soumis à la juridiction exclusive des tribunaux de Lubumbashi, RD Congo.
                                </p>
                            </div>
                        </section>

                        {/* 10. Modifications */}
                        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">10. Modifications des Conditions</h2>
                            <div className="space-y-4 text-gray-700">
                                <p>
                                    Nous nous réservons le droit de modifier ces Conditions à tout moment. Les modifications prendront effet dès leur publication sur cette page. Votre utilisation continue des Services après la publication des modifications constitue votre acceptation des nouvelles Conditions.
                                </p>
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <p className="text-blue-900 text-sm">
                                        Nous vous encourageons à consulter régulièrement cette page pour rester informé des modifications. Si vous n'acceptez pas les modifications, vous devez cesser d'utiliser nos Services.
                                    </p>
                                </div>
                            </div>
                        </section>

                        {/* 11. Contact */}
                        <section className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl shadow-sm border border-amber-200 p-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact</h2>
                            <div className="space-y-3 text-gray-700">
                                <p>Pour toute question concernant ces Conditions d'Utilisation :</p>
                                <div className="bg-white rounded-lg p-4 border border-amber-200">
                                    <p className="font-semibold text-gray-900 mb-2">Netikash</p>
                                    <p className="text-sm mb-1">
                                        <strong>Email :</strong> <a href="mailto:legal@netikash.com" className="text-amber-600 hover:underline">legal@netikash.com</a>
                                    </p>
                                    <p className="text-sm mb-1">
                                        <strong>Support :</strong> <a href="mailto:support@netikash.com" className="text-amber-600 hover:underline">support@netikash.com</a>
                                    </p>
                                    <p className="text-sm">
                                        <strong>Adresse :</strong> 1 Mpolo coin Kasangulu, Lubumbashi, RD Congo
                                    </p>
                                </div>
                            </div>
                        </section>
                    </div>

                    {/* Footer note */}
                    <div className="mt-12 text-center text-sm text-gray-500">
                        <p>Ces conditions entrent en vigueur le {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Terms;